import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button } from '@/components/common';
import { CreateDebtAdjustmentVoucherRequest, DebtAdjustmentVoucherResponse } from '@/types/debtAdjustmentVoucher';
import { debtAdjustmentVoucherApi } from '@/api/endpoints/debtAdjustmentVouchers';
import { toast } from '@/store/toastStore';
import { formatCurrency } from '@/utils/number-format';

interface CreateDebtAdjustmentVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Called with the created voucher - useful for chaining to confirm modal */
  onCreated?: (voucher: DebtAdjustmentVoucherResponse) => void;
  entityType: 'customer' | 'supplier';
  entityId: string;
  entityName?: string;
  reportPeriodFrom: string;
  reportPeriodTo: string;
  currentDebt?: number; // Current debt balance for validation
}

const CreateDebtAdjustmentVoucherModal: React.FC<CreateDebtAdjustmentVoucherModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onCreated,
  entityType,
  entityId,
  entityName,
  reportPeriodFrom,
  reportPeriodTo,
  currentDebt,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
  const [reason, setReason] = useState('');
  const [adjustmentDate, setAdjustmentDate] = useState<string>(''); // Format: YYYY-MM-DD
  const [adjustmentTime, setAdjustmentTime] = useState<string>(''); // Format: HH:mm
  const [errors, setErrors] = useState<{ adjustmentAmount?: string; reason?: string; adjustmentDate?: string }>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAdjustmentAmount('');
      setReason('');
      // Default to current date/time
      const now = new Date();
      setAdjustmentDate(now.toISOString().split('T')[0]);
      setAdjustmentTime(now.toTimeString().slice(0, 5));
      setErrors({});
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: { adjustmentAmount?: string; reason?: string } = {};

    // Validate adjustment amount
    if (!adjustmentAmount || adjustmentAmount.trim() === '') {
      newErrors.adjustmentAmount = t('debtAdjustmentVoucher.validation.amountRequired');
    } else {
      const amount = parseFloat(adjustmentAmount);
      if (isNaN(amount) || amount === 0) {
        newErrors.adjustmentAmount = t('debtAdjustmentVoucher.validation.amountNotZero');
      } else if (amount < 0) {
        // Backend only supports positive input (which will be converted to negative)
        newErrors.adjustmentAmount = 'Vui lòng nhập số dương để giảm công nợ';
      } else {
        // Check if adjustment would make debt negative
        if (currentDebt !== undefined) {
          // Convert positive to negative (for decreasing debt)
          const adjustment = -amount;
          const newDebt = currentDebt + adjustment;
          if (newDebt < 0) {
            newErrors.adjustmentAmount = t('debtAdjustmentVoucher.validation.debtCannotBeNegative');
          }
        }
      }
    }

    // Validate reason
    if (!reason || reason.trim() === '') {
      newErrors.reason = t('debtAdjustmentVoucher.validation.reasonRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const amount = parseFloat(adjustmentAmount);
      // Backend requires negative value to decrease debt
      // Convert positive input to negative (e.g., 0.01 → -0.01)
      // If user enters negative, keep it (but it has the same effect as positive)
      const adjustmentAmountValue = amount > 0 ? -amount : amount;
      
      // Validate: backend requires negative value
      if (adjustmentAmountValue >= 0) {
        setErrors({ ...errors, adjustmentAmount: 'Số tiền điều chỉnh phải là số dương' });
        setLoading(false);
        return;
      }

      // Format adjustment_date as RFC3339 (YYYY-MM-DDTHH:mm:ssZ)
      let adjustmentDateValue: string | undefined;
      if (adjustmentDate && adjustmentTime) {
        // Convert to UTC ISO string
        const dateTime = new Date(`${adjustmentDate}T${adjustmentTime}:00`);
        adjustmentDateValue = dateTime.toISOString();
      }

      const request: CreateDebtAdjustmentVoucherRequest = {
        entity_type: entityType,
        entity_id: entityId,
        adjustment_amount: adjustmentAmountValue,
        reason: reason.trim(),
        report_period_from: reportPeriodFrom,
        report_period_to: reportPeriodTo,
        adjustment_date: adjustmentDateValue, // Optional - defaults to current time if not provided
      };

      const createdVoucher = await debtAdjustmentVoucherApi.createDebtAdjustmentVoucher(request);
      toast.success(t('debtAdjustmentVoucher.messages.createSuccess'));

      onSuccess?.();
      onCreated?.(createdVoucher);
      onClose();
    } catch (error: any) {
      console.error('Error creating debt adjustment voucher:', error);
      toast.error(
        error?.response?.data?.message ||
          t('debtAdjustmentVoucher.messages.createError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Calculate new debt after adjustment for preview
  // Note: Backend requires negative AdjustmentAmount (to decrease debt)
  // - User can input positive (e.g., 0.01) → will be converted to negative (-0.01) → decreases debt
  // - User can input negative (e.g., -0.01) → stays negative → decreases debt (same effect)
  // - For preview: we show the actual effect (debt decreases by the absolute value)
  const previewNewDebt = React.useMemo(() => {
    if (!adjustmentAmount || currentDebt === undefined) return null;
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount)) return null;
    
    // Backend always uses negative value to decrease debt
    // So preview should show: currentDebt - |amount|
    const adjustmentValue = Math.abs(amount);
    const newDebt = currentDebt - adjustmentValue;
    return newDebt;
  }, [adjustmentAmount, currentDebt]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('debtAdjustmentVoucher.buttons.create')}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-4">
          {/* Entity Info (read-only) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('debtAdjustmentVoucher.fields.type')}
                </label>
                <div className="text-sm text-gray-900">
                  {entityType === 'customer' 
                    ? t('debtAdjustmentVoucher.fields.customer')
                    : t('debtAdjustmentVoucher.fields.supplier')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {entityType === 'customer' 
                    ? t('debtAdjustmentVoucher.fields.customer')
                    : t('debtAdjustmentVoucher.fields.supplier')}
                </label>
                <div className="text-sm text-gray-900">
                  {entityName || entityId}
                </div>
              </div>
            </div>
          </div>

          {/* Adjustment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('debtAdjustmentVoucher.fields.amount')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              step="0.01"
              value={adjustmentAmount}
              onChange={(e) => {
                setAdjustmentAmount(e.target.value);
                if (errors.adjustmentAmount) {
                  setErrors({ ...errors, adjustmentAmount: undefined });
                }
              }}
              placeholder="Nhập số tiền điều chỉnh (số dương để giảm công nợ)"
              disabled={loading}
              className={errors.adjustmentAmount ? 'border-red-500' : ''}
            />
            {errors.adjustmentAmount && (
              <p className="mt-1 text-sm text-red-600">{errors.adjustmentAmount}</p>
            )}
            {currentDebt !== undefined && previewNewDebt !== null && (
              <div className="mt-2 text-sm text-gray-600">
                <span>Công nợ hiện tại: </span>
                <span className="font-medium">{formatCurrency(currentDebt)}</span>
                {adjustmentAmount && !isNaN(parseFloat(adjustmentAmount)) && (
                  <>
                    <span className="mx-2">→</span>
                    <span className={`font-medium ${previewNewDebt < 0 ? 'text-red-600' : ''}`}>
                      {formatCurrency(previewNewDebt)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Adjustment Date and Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('debtAdjustmentVoucher.fields.adjustmentDate')}
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                value={adjustmentDate}
                onChange={(e) => {
                  setAdjustmentDate(e.target.value);
                  if (errors.adjustmentDate) {
                    setErrors({ ...errors, adjustmentDate: undefined });
                  }
                }}
                disabled={loading}
                className={`flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.adjustmentDate ? 'border-red-500' : ''
                }`}
              />
              <input
                type="time"
                value={adjustmentTime}
                onChange={(e) => {
                  setAdjustmentTime(e.target.value);
                  if (errors.adjustmentDate) {
                    setErrors({ ...errors, adjustmentDate: undefined });
                  }
                }}
                disabled={loading || !adjustmentDate}
                className={`w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  !adjustmentDate ? 'bg-gray-100 cursor-not-allowed' : ''
                } ${errors.adjustmentDate ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.adjustmentDate && (
              <p className="mt-1 text-sm text-red-600">{errors.adjustmentDate}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('debtAdjustmentVoucher.fields.adjustmentDateHint') || 'Ngày giờ điều chỉnh công nợ (mặc định: thời điểm hiện tại)'}
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('debtAdjustmentVoucher.fields.reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) {
                  setErrors({ ...errors, reason: undefined });
                }
              }}
              placeholder="Nhập lý do điều chỉnh công nợ..."
              disabled={loading}
              rows={4}
              className={`block w-full px-3 py-2 text-sm text-gray-900 bg-white border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${
                errors.reason ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          {/* Report Period Info (read-only) */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs text-gray-600">
              <div>
                <span className="font-medium">Kỳ báo cáo: </span>
                <span>
                  {new Date(reportPeriodFrom).toLocaleDateString('vi-VN')} - {new Date(reportPeriodTo).toLocaleDateString('vi-VN')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {t('buttons.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateDebtAdjustmentVoucherModal;

